import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useCreateCustomFormMutation, useGetScopesforFieldsQuery, useUpdateCustomFormMutation } from '../../../api/formsApiSlice';
import { useToast } from '../../toast/ToastContext';
import { FormActions, FormContainer, ModalContent, ModalOverlay } from './FormFiller';
import FormTitle from '../../form/FormTitle';
import { FieldLabel, ErrorText } from '../FieldSettings/FieldSettingsModal';
import CustomInput from '../../form/CustomInput';
import CustomTextArea from '../../form/CustomTextArea';
import StyledText from '../../StyledComponents/StyledText';
import { CorporateTheme } from '../../../utils/themes';
import CloseIcon from '../../../assets/icons/CloseIcon';
import { Button } from '../../../components/form/Button';
import DocumentIcon from '../../../assets/icons/document-text';
import { useTheme } from '../../../middleware/ThemeContext';
import { Scope } from '../../../types/types';
import AMSSelect from '../../AMSSelect';

interface CreateFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (formId: number, updated?: { name: string; description?: string; scope?: number; formPages?: any[] }) => void;
  mode?: 'create' | 'edit';
  initialData?: { id: number; name: string; description?: string; scope?: number; formPages?: any[] };
}

const CreateFormModal: React.FC<CreateFormModalProps> = ({ isOpen, onClose, onSuccess, mode = 'create', initialData }) => {
  const { theme } = useTheme();
  const showToast = useToast();
  const [{ name, description, scope }, setStateFormDetails] = useState<{
    name: string;
    description: string;
    scope: number | undefined;
  }>({
    name: '',
    description: '',
    scope: undefined,
  });
  const [allScopes, setAllScopes] = useState<{ label: string; value: string }[]>([]);
  const scopesAdditionalParams = useMemo(() => ({ isVisible: true }), []);

  const [nameError, setNameError] = useState<string | null>(null);
  const [scopeError, setScopeError] = useState<string | null>(null);

  const [createCustomForm, { isLoading }] = useCreateCustomFormMutation();
  const [updateCustomForm, { isLoading: isUpdating }] = useUpdateCustomFormMutation();
  const { data: scopes } = useGetScopesforFieldsQuery({ isVisible: true });

  const mapScopesResponse = useCallback((json: any, pageNumber: number) => {
    const scopesList = json?.scopes ?? [];
    const perPage = (json?.perPage ?? json?.pagination?.perPage ?? scopesList.length) || 1;
    const totalCount = json?.totalCount ?? json?.pagination?.total ?? scopesList.length;
    const hasMore = totalCount ? pageNumber * perPage < totalCount : scopesList.length === perPage;

    const options = scopesList.map((scope: Scope) => ({
      label: scope?.displayName,
      value: String(scope?.id ?? ''),
      ...scope,
    }));

    return {
      options,
      hasMore,
      additional: { page: pageNumber + 1 },
    };
  }, []);

  const selectedScopeOption = useMemo(() => {
    if (scope == null) return null;
    const currentValue = String(scope);
    const existing = allScopes.find((item) => String(item.value) === currentValue);
    const label = existing?.label || `Scope ${currentValue}`;
    return { value: currentValue, label };
  }, [scope, allScopes]);

  const handleScopeSelect = (option: any) => {
    const selectedOption = Array.isArray(option) ? option[0] : option;
    const rawValue = selectedOption?.value;
    const parsedValue = rawValue !== undefined && rawValue !== null ? Number(rawValue) : undefined;
    setStateFormDetails((prev) => ({ ...prev, scope: Number.isNaN(parsedValue) ? undefined : parsedValue }));
    if (parsedValue !== undefined && !Number.isNaN(parsedValue)) {
      setScopeError(null);
      if (selectedOption?.label) {
        setAllScopes((prev) => {
          const exists = prev.some((item) => String(item.value) === String(rawValue));
          if (exists) return prev;
          return [...prev, { label: selectedOption.label, value: String(rawValue) }];
        });
      }
    }
  };

  const resetForm = () => {
    setStateFormDetails({
      name: '',
      description: '',
      scope: undefined,
    });
    setNameError(null);
    setScopeError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  useEffect(() => {
    if (isOpen && mode === 'edit' && initialData) {
      setStateFormDetails({
        name: initialData.name || '',
        description: initialData.description || '',
        scope: initialData.scope,
      });
      setNameError(null);
      setScopeError(null);
    }
  }, [isOpen, mode, initialData]);

  useEffect(() => {
    if (!scopes?.scopes) return;
    const newOptions = scopes.scopes.map((scopeItem: Scope) => ({
      label: scopeItem?.displayName,
      value: String(scopeItem?.id ?? ''),
    }));
    setAllScopes(newOptions);
  }, [scopes]);

  const validateForm = () => {
    let isValid = true;
    setNameError(null);
    setScopeError(null);

    if (!name.trim()) {
      setNameError('Form title is required');
      isValid = false;
    }

    if (mode === 'create' && !scope) {
      setScopeError('Please select a scope for the form');
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (mode === 'edit' && initialData?.id) {
        const payload: any = { id: initialData.id, name: name.trim(), description };
        if (typeof scope !== 'undefined') payload.scope = scope; // keep existing
        if (Array.isArray(initialData.formPages)) payload.formPages = initialData.formPages;
        const res = await updateCustomForm(payload).unwrap();
        onSuccess(initialData.id, { name: name.trim(), description, scope, formPages: initialData.formPages });
        showToast("Form Updated", "The form has been updated.", "success");
      } else {
        const data = await createCustomForm({ name, description, scope }).unwrap();
        if (data) {
          onSuccess(data?.id, { name: name.trim(), description, scope });
          resetForm();
          showToast("Custom Form Created", "The custom form has been created.", "success");
        }
      }
    } catch (err: any) {
      console.error(mode === 'edit' ? "Form update error:" : "Form creation error:", err);

      const message =
        err?.data?.response?.errors ||
        err?.data?.response?.message ||
        err?.error ||
        (mode === 'edit' ? "Failed to update form. Please try again." : "Failed to create custom form. Please try again.");

      showToast(mode === 'edit' ? "Form Update Failed" : "Form Creation Failed", message, "error");
    }

  };

  return (
    <Modal show={isOpen} onHide={handleClose} className='border-0'>
      <ModalOverlay>
        <ModalContent theme={theme}>
          <FormTitle title={mode === 'edit' ? 'Edit Form' : 'Create New Form'} />
          <FormContainer theme={theme}>
            <StyledText
              fontSize="14px"
              fontWeight={500}
              color={CorporateTheme.colors.textSecondary}
              style={{ fontStyle: 'italic' }}
              className="text-center m-4"
            >
              {mode === 'edit' ? 'Update the form title and description. Scope is shown for reference and cannot be changed.' : 'Enter form title, form description and select the scope for this form to create new form'}
            </StyledText>

            {/* Form Title */}
            <div className="px-3 py-2">
              <FieldLabel theme={theme}>
                Form Title <span style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>*</span>
              </FieldLabel>
              <CustomInput
                type="text"
                placeholder="Enter form name..."
                value={name}
                onChange={(value: string) => {
                  setStateFormDetails((prev) => ({ ...prev, name: value }));
                  setNameError(null);
                }}
                error={nameError ?? undefined}   // 🔥 Pass error directly
                required
              />
            </div>

            {/* Form Scope */}
            <div className="px-3 py-2">
              <FieldLabel theme={theme}>
                Form Scope {mode !== 'edit' && <span style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>*</span>}
              </FieldLabel>
              <AMSSelect
                endpoint="/scopes"
                placeholder="Select scope"
                value={selectedScopeOption ?? null}
                onChange={handleScopeSelect}
                mapResponse={mapScopesResponse}
                additionalParams={scopesAdditionalParams}
                isDisabled={mode === 'edit'}
                isSearchable={false}
                defaultOptions
                error={mode === 'edit' ? undefined : (scopeError ?? undefined)}
              />
            </div>

            {/* Form Description */}
            <div className="px-3 py-2">
              <FieldLabel theme={theme}>Form Description</FieldLabel>
              <CustomTextArea
                type="textarea"
                placeholder="Enter form description..."
                value={description}
                onChange={(value: string) =>
                  setStateFormDetails((prev) => ({ ...prev, description: value }))
                }
              />
            </div>
          </FormContainer>

          {/* Actions */}
          <FormActions>
            <Button theme={theme} type="button" $variant="cancel" onClick={handleClose} disabled={isLoading || isUpdating}>
              <CloseIcon color={theme.CorporateTheme.colors.textSecondary} /> Cancel
            </Button>
            <Button theme={theme} type="submit" $variant="add" onClick={handleSubmit} disabled={isLoading || isUpdating}>
              <DocumentIcon color={theme.CorporateTheme.colors.background} />
              {mode === 'edit' ? (isUpdating ? 'Saving...' : 'Save Changes') : (isLoading ? 'Creating...' : 'Create Form')}
            </Button>
          </FormActions>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};

export default CreateFormModal;
